import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Table } from 'rsuite';
import { has, set, get, reduce, split, upperFirst } from 'lodash/fp';
import * as StatusActions from '../../../constants/reservation/StatusActions';
import Pagination from '../../Pagination';
import DateCell from '../../widgets/custom-cell/Date';
import CustomHeaderCell from '../../widgets/custom-cell/HeaderCell';
import PathKeyCell from '../../widgets/custom-cell/PathKey';
import PathKeyOrCell from '../../widgets/custom-cell/PathKeyOr';
import ActionCell from './ActionCell';
import StatusCell from './StatusCell';
import ProgramsCell from './ProgramsCell';
import NotesCell from './NotesCell';
import PaginationType from '../../../interfaces/PaginationType';
import NotifyGuestConfirmationModal from '../NotifyGuestConfirmationModal';
import CancelReservationConfirmationModal from '../CancelReservationConfirmationModal';

const { Column, Cell, HeaderCell } = Table;

class ReservationList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showNotifyGuestDialog: false,
      showCancelDialog: false,
      cancellationReason: '',
      selectedItem: null,
      emailInput: null,
      action: null
    };
  }

  componentDidMount() {
    const { onInit, query } = this.props;
    onInit(query);
  }

  getData() {
    const { items } = this.props;

    return items;
  }

  handleSortColumn = (sortColumn, sortType) => {
    const { onSort, query } = this.props;
    onSort(sortColumn, sortType, query);
  };

  handleChangePage = page => {
    const { onChangePage, loadedPages, query } = this.props;
    const isPageLoaded = has(page, loadedPages);
    const nextPageQuery = set('page', page, query);
    onChangePage(nextPageQuery, isPageLoaded);
  };

  handleChangeLimit = limit => {
    const { onChangeLimit, query } = this.props;
    const newQuery = { ...query, limit, page: 1 };
    onChangeLimit(newQuery);
  };

  handleOnChangeEmail = value => {
    this.setState({ emailInput: value });
  };

  handleOnApprove = reservation => {
    const {
      isNotifyGuestEnabled,
      onApprove,
      hasActiveSmtpAccounts
    } = this.props;
    if (isNotifyGuestEnabled && hasActiveSmtpAccounts)
      this.setState({
        showNotifyGuestDialog: true,
        selectedItem: reservation,
        action: StatusActions.APPROVE
      });
    else {
      onApprove(reservation.id);
    }
  };

  handleOnCancel = reservation => {
    this.setState({
      showCancelDialog: true,
      selectedItem: reservation,
      action: StatusActions.CANCEL
    });
  };

  handleConfirmCancelDialog = reason => {
    const {
      isNotifyGuestEnabled,
      onCancel,
      hasActiveSmtpAccounts
    } = this.props;
    const { selectedItem } = this.state;

    if (isNotifyGuestEnabled && hasActiveSmtpAccounts) {
      this.setState({
        showCancelDialog: false,
        showNotifyGuestDialog: true,
        cancellationReason: reason
      });
      return;
    }

    onCancel(selectedItem.id, false, null, reason);
    this.setState({
      showCancelDialog: false,
      cancellationReason: '',
      selectedItem: null,
      action: null
    });
  };

  handleCloseCancelDialog = () => {
    this.setState({
      showCancelDialog: false,
      cancellationReason: '',
      selectedItem: null,
      action: null
    });
  };

  handleOnReject = reservation => {
    const {
      isNotifyGuestEnabled,
      onReject,
      hasActiveSmtpAccounts
    } = this.props;
    if (isNotifyGuestEnabled && hasActiveSmtpAccounts)
      this.setState({
        showNotifyGuestDialog: true,
        selectedItem: reservation,
        action: StatusActions.REJECT
      });
    else {
      onReject(reservation.id);
    }
  };

  updateStatus = (notifyGuest = false) => {
    const { selectedItem, action, emailInput, cancellationReason } = this.state;
    const { onApprove, onCancel, onReject } = this.props;
    switch (action) {
      case StatusActions.APPROVE:
        onApprove(selectedItem.id, notifyGuest, emailInput);
        break;
      case StatusActions.REJECT:
        onReject(selectedItem.id, notifyGuest, emailInput);
        break;
      case StatusActions.CANCEL:
        onCancel(selectedItem.id, notifyGuest, emailInput, cancellationReason);
        break;
      default:
        break;
    }
    this.setState({
      showNotifyGuestDialog: false,
      cancellationReason: '',
      selectedItem: null,
      action: null
    });
  };

  handleConfirmNotifyGuestDialog = () => {
    const notifyGuest = true;
    this.updateStatus(notifyGuest);
  };

  handleSkipNotifyGuestDialog = () => {
    const notifyGuest = false;
    this.updateStatus(notifyGuest);
  };

  handleCancelNotifyGuestDialog = () => {
    this.setState({
      showNotifyGuestDialog: false,
      cancellationReason: '',
      selectedItem: null,
      action: null
    });
  };

  renderPagination() {
    const { pagination } = this.props;
    return (
      <Pagination
        page={pagination.currentPage}
        limit={pagination.limit}
        total={pagination.totalCount}
        onChangePage={this.handleChangePage}
        onChangeLimit={this.handleChangeLimit}
      />
    );
  }

  render() {
    const {
      isFetching,
      isSaving,
      query,
      actions,
      onUnlink,
      hasActiveSmtpAccounts,
      showNotes
    } = this.props;
    const {
      showNotifyGuestDialog,
      showCancelDialog,
      selectedItem
    } = this.state;
    const [sortColumn, sortType] = split(':', get('order', query));
    const actionsProps = reduce(
      (all, action) => ({
        ...all,
        [`show${upperFirst(action)}`]: true
      }),
      {},
      actions
    );

    return (
      <Fragment>
        <Table
          height={420}
          loading={isFetching}
          data={this.getData()}
          sortColumn={sortColumn}
          sortType={sortType}
          onSortColumn={this.handleSortColumn}
          bordered
        >
          <Column width={100} sortable>
            <CustomHeaderCell>ID</CustomHeaderCell>
            <Cell dataKey="id" />
          </Column>
          <Column width={150} sortable>
            <CustomHeaderCell>Date/Time</CustomHeaderCell>
            <DateCell dataKey="requestTime" format="MM/DD/YYYY hh:mm A" />
          </Column>
          <Column width={90} sortable>
            <CustomHeaderCell>Guest ID</CustomHeaderCell>
            <Cell dataKey="guestId" />
          </Column>
          <Column width={160} sortable resizable>
            <CustomHeaderCell>First Name</CustomHeaderCell>
            <PathKeyOrCell dataKey="firstName" orDataKey="guest.firstname" />
          </Column>
          <Column width={160} sortable resizable>
            <CustomHeaderCell>Last Name</CustomHeaderCell>
            <PathKeyOrCell dataKey="lastName" orDataKey="guest.lastname" />
          </Column>
          <Column width={90}>
            <CustomHeaderCell>Status</CustomHeaderCell>
            <StatusCell dataKey="status" />
          </Column>
          <Column width={90}>
            <CustomHeaderCell>Barcode</CustomHeaderCell>
            <Cell dataKey="code" align="center" />
          </Column>
          <Column width={150} resizable>
            <CustomHeaderCell>Outreach</CustomHeaderCell>
            <PathKeyCell dataKey="outreach.name" />
          </Column>
          <Column width={200} resizable>
            <CustomHeaderCell>Help Desired</CustomHeaderCell>
            <ProgramsCell dataKey="programs" />
          </Column>
          {showNotes && (
            <Column width={250} resizable>
              <CustomHeaderCell>Notes</CustomHeaderCell>
              <NotesCell dataKey="note" />
            </Column>
          )}
          <Column width={310} fixed="right">
            <HeaderCell align="center">Action</HeaderCell>
            <ActionCell
              {...actionsProps}
              align="center"
              dataKey="id"
              isSaving={isSaving}
              handleApprove={this.handleOnApprove}
              handleReject={this.handleOnReject}
              handleCancel={this.handleOnCancel}
              handleUnlink={onUnlink}
            />
          </Column>
        </Table>

        {this.renderPagination()}
        {hasActiveSmtpAccounts && (
          <NotifyGuestConfirmationModal
            show={showNotifyGuestDialog}
            email={
              get('guest.email', selectedItem) ||
              get('email', selectedItem) ||
              ''
            }
            showEmailField
            onConfirm={this.handleConfirmNotifyGuestDialog}
            onSkip={this.handleSkipNotifyGuestDialog}
            onCancel={this.handleCancelNotifyGuestDialog}
            onChangeEmail={this.handleOnChangeEmail}
          />
        )}
        <CancelReservationConfirmationModal
          show={showCancelDialog}
          onCancel={this.handleCloseCancelDialog}
          onConfirm={this.handleConfirmCancelDialog}
        />
      </Fragment>
    );
  }
}

ReservationList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isFetching: PropTypes.bool.isRequired,
  onInit: PropTypes.func.isRequired,
  query: PropTypes.shape({}).isRequired,
  loadedPages: PropTypes.shape({}).isRequired,
  pagination: PaginationType.isRequired,
  onChangePage: PropTypes.func.isRequired,
  onChangeLimit: PropTypes.func.isRequired,
  onSort: PropTypes.func.isRequired,
  isNotifyGuestEnabled: PropTypes.bool,
  actions: PropTypes.arrayOf(PropTypes.string),
  onApprove: PropTypes.func,
  onReject: PropTypes.func,
  onCancel: PropTypes.func,
  onUnlink: PropTypes.func,
  isSaving: PropTypes.bool,
  hasActiveSmtpAccounts: PropTypes.bool,
  showNotes: PropTypes.bool
};

ReservationList.defaultProps = {
  actions: [],
  onApprove: () => {},
  onReject: () => {},
  onCancel: () => {},
  onUnlink: () => {},
  isSaving: false,
  isNotifyGuestEnabled: false,
  hasActiveSmtpAccounts: false,
  showNotes: false
};

export default ReservationList;
